'use client';

import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, MessageSquare } from 'lucide-react';

const CONTACT_INFO = [
  {
    icon: Phone,
    title: 'Call Us',
    detail: '+91 97421 00030',
    sub: 'Mon–Sat, 9 AM – 7 PM IST',
    href: 'tel:+919742100030',
    color: 'text-[#1a3a8f]',
    bg: 'bg-[#1a3a8f]/8',
  },
  {
    icon: Mail,
    title: 'Email Us',
    detail: 'info@wikasedu.com',
    sub: 'We reply within 24 hours',
    href: 'mailto:info@wikasedu.com',
    color: 'text-success',
    bg: 'bg-success/8',
  },
  {
    icon: MapPin,
    title: 'Our Office',
    detail: 'Bangalore, Karnataka',
    sub: 'India — 560001',
    href: '#',
    color: 'text-warning',
    bg: 'bg-warning/8',
  },
  {
    icon: Clock,
    title: 'Working Hours',
    detail: 'Mon – Sat',
    sub: '9:00 AM – 7:00 PM IST',
    href: '#',
    color: 'text-[#1a3a8f]',
    bg: 'bg-[#1a3a8f]/8',
  },
];

export default function ContactUsPage() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      setError('Name, phone, and message are required.');
      return;
    }
    if (!/^\d{10}$/.test(form.phone.trim())) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/save-counselling', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'Contact Us Page' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit');
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen mesh-bg">
      <Navbar />
      <PageTransition>
        {/* Hero */}
        <section className="pt-28 pb-16 bg-gradient-to-br from-[#f0f4ff] to-[#e8eeff]">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a3a8f]/10 border border-[#1a3a8f]/20 rounded-full text-sm text-[#1a3a8f] font-semibold mb-6">
              <MessageSquare size={14} />
              Contact Us
            </div>
            <h1 className="text-[38px] font-extrabold text-foreground tracking-tight mb-4">
              We&apos;re Here to{' '}
              <span className="hero-gradient-text">Help You</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Have questions about NEET counselling, college admissions, or our services? Reach out — our expert team responds within 24 hours.
            </p>
          </div>
        </section>

        {/* Contact Cards */}
        <section className="py-14">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
              {CONTACT_INFO.map((info) => (
                <a
                  key={info.title}
                  href={info.href}
                  className="p-6 glass-card rounded-2xl border border-[#1a3a8f]/10 hover:shadow-md transition-all duration-200 group"
                >
                  <div className={`w-11 h-11 rounded-xl ${info.bg} flex items-center justify-center mb-4`}>
                    <info.icon size={20} className={info.color} />
                  </div>
                  <h3 className="font-bold text-foreground text-sm mb-1">{info.title}</h3>
                  <p className="text-[#1a3a8f] font-semibold text-sm mb-1">{info.detail}</p>
                  <p className="text-xs text-muted-foreground">{info.sub}</p>
                </a>
              ))}
            </div>

            {/* Form + Map */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Form */}
              <div className="glass-card rounded-3xl p-8 border border-[#1a3a8f]/10">
                <h2 className="text-2xl font-extrabold text-foreground mb-2">Send Us a Message</h2>
                <p className="text-muted-foreground text-sm mb-6">Fill in the form and we&apos;ll get back to you within 24 hours.</p>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 size={32} className="text-success" />
                    </div>
                    <h3 className="text-xl font-extrabold text-foreground mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground text-sm">Thank you, <strong>{form.name}</strong>! We&apos;ll reach out to you at <strong>{form.phone}</strong> within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Full Name <span className="text-red-500">*</span></label>
                        <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your name" className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3a8f]/30 bg-slate-50/50" required />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Phone <span className="text-red-500">*</span></label>
                        <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit number" maxLength={10} className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3a8f]/30 bg-slate-50/50" required />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Email</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3a8f]/30 bg-slate-50/50" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Subject</label>
                      <select name="subject" value={form.subject} onChange={handleChange} className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3a8f]/30 bg-slate-50/50 text-foreground">
                        <option value="">Select a subject</option>
                        <option value="NEET Counselling">NEET Counselling</option>
                        <option value="College Admission">College Admission</option>
                        <option value="AIQ Counselling">AIQ Counselling</option>
                        <option value="State Quota">State Quota</option>
                        <option value="Deemed University">Deemed University</option>
                        <option value="General Enquiry">General Enquiry</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Message <span className="text-red-500">*</span></label>
                      <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us how we can help you..." rows={4} className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3a8f]/30 bg-slate-50/50 resize-none" required />
                    </div>
                    {error && <p className="text-red-500 text-xs">{error}</p>}
                    <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 py-3">
                      {loading ? (
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Info Panel */}
              <div className="space-y-6">
                <div className="p-8 bg-[#0a1f5c] rounded-3xl text-white">
                  <h3 className="text-xl font-extrabold mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <a href="tel:+919742100030" className="flex items-center gap-3 text-blue-200 hover:text-white transition-colors">
                      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <Phone size={18} className="text-blue-300" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-300 font-medium">Call us directly</p>
                        <p className="font-bold text-white">+91 97421 00030</p>
                      </div>
                    </a>
                    <a href="mailto:info@wikasedu.com" className="flex items-center gap-3 text-blue-200 hover:text-white transition-colors">
                      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <Mail size={18} className="text-blue-300" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-300 font-medium">Email us</p>
                        <p className="font-bold text-white">info@wikasedu.com</p>
                      </div>
                    </a>
                    <div className="flex items-center gap-3 text-blue-200">
                      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <MapPin size={18} className="text-blue-300" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-300 font-medium">Visit us</p>
                        <p className="font-bold text-white">Bangalore, Karnataka, India</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 glass-card rounded-2xl border border-[#1a3a8f]/10">
                  <h3 className="font-bold text-foreground mb-3">Frequently Asked Questions</h3>
                  <div className="space-y-3">
                    {[
                      { q: 'Is the first counselling session free?', a: 'Yes! Your first counselling session with our expert is completely free.' },
                      { q: 'How quickly will I get a response?', a: 'Our team responds within 24 hours on all working days.' },
                      { q: 'Do you cover all states?', a: 'Yes, we cover all 28 states and AIQ counselling across India.' },
                    ].map((faq) => (
                      <div key={faq.q} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                        <p className="text-sm font-semibold text-foreground mb-1">{faq.q}</p>
                        <p className="text-xs text-muted-foreground">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </PageTransition>
      <Footer />
    </main>
  );
}
